import { useState } from 'react';
import { clsx } from 'clsx';
import { useScrollReveal } from '@/lib/useScrollReveal';

/**
 * Inline FAQ. Generates FAQPage JSON-LD for rich SERP eligibility.
 * Answer divs always live in the DOM (max-height transition) so the
 * structured data is indexable even when collapsed.
 *
 * Premium polish: refined card with glass tint, gradient active state,
 * animated chevron, scroll-reveal.
 */
const QA: { q: string; a: string }[] = [
  {
    q: 'Is ACES free?',
    a: 'Yes. Every chapter, every simulation, every practice set. We run a small number of unobtrusive ads (Google AdSense) to cover the cost of running the site. You do not need to log in or sign up.',
  },
  {
    q: 'Is ACES affiliated with NCERT, CBSE, or the Kerala SCERT board?',
    a: 'No. ACES is an independent learning platform. We follow the public Higher Secondary commerce syllabi as specifications — what topics to teach, at what depth — and write all content independently from scratch.',
  },
  {
    q: 'Which boards and classes are covered?',
    a: 'Class 11 and Class 12 commerce stream — Accountancy, Business Studies, and Economics — for two boards: CBSE (NCERT-aligned) and Kerala HSE (SCERT-aligned). 137 chapters in total across both boards.',
  },
  {
    q: 'Are the practice answers verified?',
    a: 'Every chapter goes through a verification pass, including its practice answers. If a chapter is still being re-checked, it carries an "Under review" badge, so you can see its status.',
  },
  {
    q: 'Can I use ACES instead of my textbook?',
    a: "ACES is a powerful supplement, not a substitute. For board exams, you should still cross-check key topics with your prescribed textbook and your teacher. Use ACES for the interactive parts — the journal entries, the ledger simulation, the practice sets — that the textbook can't give you.",
  },
  {
    q: "How is this different from Byju's or Physics Wallah?",
    a: "Those platforms are mostly video lectures plus past-year question banks. ACES is interactive: you post the journal entries yourself, you watch the ledger update in real time, you work through graduated hints until the concept clicks. Different shape, different muscles trained.",
  },
  {
    q: "How does the AI tutor work, and what won't it do?",
    a: "Each chapter has graduated-hint chains tied to specific misconceptions. Click for a hint and you get a nudge towards the right rule. Click again and you get a re-framing question. Click a third time and you get a small step. At no point does the tutor reveal the final answer — by design.",
  },
  {
    q: 'Is my data being collected?',
    a: "We do not require login. In-page interaction events stay in your browser's session storage. Aggregate, anonymised usage is recorded via Google Analytics. See our Privacy Policy for the full plain-English version.",
  },
];

export function FAQ() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  const ref = useScrollReveal<HTMLDivElement>({ threshold: 0.05 });

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: QA.map(({ q, a }) => ({
              '@type': 'Question',
              name: q,
              acceptedAnswer: { '@type': 'Answer', text: a },
            })),
          }),
        }}
      />
      <div ref={ref}>
        <ul className="rounded-card-lg border border-ink-200/80 bg-cream shadow-card divide-y divide-ink-100 overflow-hidden">
          {QA.map(({ q, a }, i) => {
            const open = openIdx === i;
            return (
              <li key={q}>
                <button
                  onClick={() => setOpenIdx(open ? null : i)}
                  className={clsx(
                    'w-full text-left px-5 sm:px-6 py-[1.05rem] flex items-center justify-between gap-4 transition-colors duration-150',
                    open
                      ? 'bg-gradient-to-r from-accent-50/80 to-transparent'
                      : 'hover:bg-ink-50/60',
                  )}
                  aria-expanded={open}
                >
                  <span className={clsx('font-semibold text-[15px] leading-snug', open ? 'text-accent-700' : 'text-ink-900')}>
                    {q}
                  </span>
                  {/* Rotating chevron — 180° when open */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    className={clsx(
                      'w-4 h-4 shrink-0 transition-transform duration-200',
                      open ? 'rotate-180 text-accent-500' : 'text-ink-400',
                    )}
                  >
                    <path d="M5 8l5 5 5-5" />
                  </svg>
                </button>
                <div
                  className="overflow-hidden transition-[max-height] duration-200 ease-out"
                  style={{ maxHeight: open ? '36rem' : '0' }}
                >
                  <div className="pl-4 pr-2 pb-5 pt-0.5 text-ink-600 text-[15px] leading-relaxed border-l-2 border-accent-200 ml-5 mr-5 sm:ml-6 sm:mr-6 mb-1">
                    {a}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
}
